import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RecipesTranslationFieldsContainerComponent } from '@recipes/recipes-translation/recipes-translation-fields-container/recipes-translation-fields-container.component';
import { RecipesTranslationFieldsHeaderComponent } from '@recipes/recipes-translation/recipes-translation-fields-header/recipes-translation-fields-header.component';
import {
  FieldsOptions,
  RecipesTranslationInputFieldsComponent,
} from '@recipes/recipes-translation/recipes-translation-input-fields/recipes-translation-input-fields.component';
import { RecipesTranslationSectionComponent } from '@recipes/recipes-translation/recipes-translation-section/recipes-translation-section.component';
import { RecipesTranslationVariantsComponent } from '@recipes/recipes-translation/recipes-translation-variants/recipes-translation-variants.component';
import { MissingInputError } from '@shared/errors/missing-input/missing-input.error';
import { LocalizedKeyValue } from '@shared/models/dataref.model';
import { EditStepLite } from '@shared/models/recipe/recipe-lite.model';
import { RecipeTranslationVariant } from '@shared/models/recipe/recipes-translation-form.model';
import { PipesModule } from '@shared/pipes/pipes.module';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface VariantInfos {
  domain: string;
  market: string;
  lang: string;
  variants: RecipeTranslationVariant[];
}

export type StepFields = {
  variants: string[];
} & Pick<FieldsOptions, 'targetControl' | 'sourceControl'>;

@Component({
  selector: 'app-recipes-translation-steps',
  templateUrl: './recipes-translation-steps.component.html',
  styleUrls: ['./recipes-translation-steps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RecipesTranslationFieldsContainerComponent,
    RecipesTranslationSectionComponent,
    NgForOf,
    PipesModule,
    TranslateModule,
    NgForOf,
    NgIf,
    RecipesTranslationFieldsHeaderComponent,
    RecipesTranslationInputFieldsComponent,
    AsyncPipe,
    RecipesTranslationVariantsComponent,
  ],
})
export class RecipesTranslationStepsComponent implements OnInit {
  @Input() stepsInfos: {
    source: VariantInfos;
    target: VariantInfos;
    sourceSteps: EditStepLite[];
    stepTypes: Record<string, LocalizedKeyValue>;
  };

  stepsFields: StepFields[][] = [];

  constructor(private readonly translateService: TranslateService) {}

  ngOnInit(): void {
    if (!this.stepsInfos) {
      throw new MissingInputError('stepsInfos');
    }

    this.mapRecipeToField();
  }

  translatedStepType(type: string): string {
    return this.stepsInfos.stepTypes[type]?.name[this.stepsInfos.source.market][this.stepsInfos.source.lang] ?? type;
  }

  getHeaderLabels(stepFields: StepFields[]): Observable<{
    left: string;
    right?: string;
  }> {
    const hasShortDescription = stepFields.some((stepField) => !!stepField.sourceControl.right);
    const DESCRIPTION_KEY = 'RECIPE_TRANSLATION.FIELDS_LABELS.DESCRIPTION';
    const SHORT_DESCRIPTION_KEY = 'RECIPE_TRANSLATION.FIELDS_LABELS.SHORT_DESCRIPTION';

    return this.translateService.get([DESCRIPTION_KEY, SHORT_DESCRIPTION_KEY]).pipe(
      map((translations) => ({
        left: translations[DESCRIPTION_KEY],
        ...(hasShortDescription && {
          right: translations[SHORT_DESCRIPTION_KEY],
        }),
      })),
    );
  }

  private mapRecipeToField(): void {
    this.stepsInfos.source.variants.forEach((sourceRecipe, index) => {
      const sourceSteps = sourceRecipe.form.controls.steps.controls;
      const targetSteps = this.stepsInfos.target.variants[index].form.controls.steps.controls;

      sourceSteps.forEach((step, ind) => {
        const fields: StepFields = {
          variants: [String(sourceRecipe.quantity)],
          sourceControl: {
            left: step.controls.description,
            right: step.controls.shortDescription,
          },
          targetControl: {
            left: targetSteps[ind].controls.description,
            right: targetSteps[ind].controls.shortDescription,
          },
        };

        this.stepsFields[ind] = this.stepsFields[ind] ?? [];
        this.stepsFields[ind].push(fields);
      });
    });
  }
}
